import { useState, useEffect, useCallback } from 'react';
import { X, SlidersHorizontal, ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { currencySymbol } from '@/lib/currency';
import { useGradeBands, useLanguages, useCurricula } from '@/hooks/useValueCatalog';
import { useExamCatalog } from '@/hooks/useExamCatalog';

export interface FiltersState {
  sortBy: string;
  priceMin: number | null;
  priceMax: number | null;
  offeringType: string;
  gradeBand: string[];
  language: string[];
  curriculum: string[];
  availabilityTimeOfDay: string;
  trialSessionAvailable: boolean | null;
  targetExamId: string;
  topicKeywords: string;
}

export const DEFAULT_FILTERS: FiltersState = {
  sortBy: 'RELEVANCE',
  priceMin: null,
  priceMax: null,
  offeringType: 'ALL',
  gradeBand: [],
  language: [],
  curriculum: [],
  availabilityTimeOfDay: '',
  trialSessionAvailable: null,
  targetExamId: '',
  topicKeywords: '',
};

const AVAILABILITY_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: 'MORNING', label: 'Morning · 6am–12pm' },
  { value: 'AFTERNOON', label: 'Afternoon · 12–5pm' },
  { value: 'EVENING', label: 'Evening · 5–9pm' },
];

const RADIUS_OPTIONS = [
  { value: 5,   label: 'Within 5 km' },
  { value: 10,  label: 'Within 10 km' },
  { value: 25,  label: 'Within 25 km' },
  { value: 50,  label: 'Within 50 km' },
  { value: 100, label: 'Within 100 km' },
];

const DEFAULT_RADIUS_KM = 25;

interface FilterPanelProps {
  filters: FiltersState;
  onFiltersChange: (patch: Partial<FiltersState>) => void;
  priceMax?: number;
  /** Display-only — server-resolved (student profile or geo header), never user-editable */
  currency?: string;
  /** Whether the user has selected a location — shows the Distance filter when true */
  hasLocation?: boolean;
  /** Current radius in km (only relevant when hasLocation is true) */
  radiusKm?: number;
  /** Called when the user selects a different radius */
  onRadiusChange?: (km: number) => void;
  /** Hide the offering-type chip section — used on per-type search pages where the type is fixed by the route. */
  hideOfferingType?: boolean;
  /** Override the Sort-by options (defaults to the teacher-search set). */
  sortItems?: { value: string; label: string }[];
  /** Extra filter sections rendered inside the panel card (e.g. per-offering-type filters), in matching style. */
  extraSections?: React.ReactNode;
  /** Hide the free-text "Topic keywords" section — offering search replaces it with a subject-scoped topics multi-select. */
  hideTopicKeywords?: boolean;
}

const DEFAULT_SORT_ITEMS = [
  { value: 'RELEVANCE', label: 'Relevance' },
  { value: 'PRICE_LOW', label: 'Price: Low to High' },
  { value: 'PRICE_HIGH', label: 'Price: High to Low' },
  { value: 'EXPERIENCE', label: 'Experience' },
];

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
      {children}
    </p>
  );
}

export function Divider() {
  return <div className="h-px bg-border/50 -mx-5" />;
}

export function ChipToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer',
        active
          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
          : 'bg-background text-foreground border-border hover:border-primary/40 hover:bg-primary/5'
      )}
    >
      {label}
    </button>
  );
}

interface MultiSelectChipsProps {
  options: { code: string; label: string }[];
  selected: string[];
  onToggle: (code: string) => void;
  placeholder: string;
}

export function MultiSelectChips({ options, selected, onToggle, placeholder }: MultiSelectChipsProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className="flex w-full items-center justify-between h-9 rounded-md border border-input bg-background px-3 text-sm cursor-pointer hover:border-primary/40 transition-colors"
          >
            <span className={cn(selected.length === 0 && 'text-muted-foreground')}>
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>No options found.</CommandEmpty>
              <CommandGroup>
                {options.map(opt => {
                  const isSelected = selected.includes(opt.code);
                  return (
                    <CommandItem
                      key={opt.code}
                      value={opt.label}
                      onSelect={() => onToggle(opt.code)}
                      className="cursor-pointer"
                    >
                      <span
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </span>
                      {opt.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Selected values are shown consistently for ALL filters via the top ActiveFilterChips
          bar, not inline here — the trigger's "N selected" is the in-panel indicator. */}
    </div>
  );
}

function RadioOption({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={cn(
        'flex items-center gap-2.5 w-full rounded-lg px-2.5 py-2 text-sm transition-colors duration-150 cursor-pointer text-left',
        checked ? 'bg-primary/8 text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
      )}
    >
      <span className={cn(
        'w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
        checked ? 'border-primary bg-primary' : 'border-border bg-background'
      )}>
        {checked && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
      </span>
      {label}
    </button>
  );
}

export function FilterPanel({ filters, onFiltersChange, priceMax = 5000, currency, hasLocation = false, radiusKm = DEFAULT_RADIUS_KM, onRadiusChange, hideOfferingType = false, sortItems = DEFAULT_SORT_ITEMS, extraSections, hideTopicKeywords = false }: FilterPanelProps) {
  const gradeBands = useGradeBands();
  const languages = useLanguages();
  const curricula = useCurricula();
  const { exams: targetExams, loading: targetExamsLoading } = useExamCatalog();

  const [localPrice, setLocalPrice] = useState<[number, number]>([
    filters.priceMin ?? 0,
    filters.priceMax ?? priceMax,
  ]);

  useEffect(() => {
    setLocalPrice(prev => {
      const newMin = filters.priceMin ?? 0;
      const newMax = filters.priceMax ?? priceMax;
      if (prev[0] === newMin && prev[1] === newMax) return prev;
      return [newMin, newMax];
    });
  }, [filters.priceMin, filters.priceMax, priceMax]);

  const toggleArray = useCallback(
    (key: 'gradeBand' | 'language' | 'curriculum', value: string) => {
      const current = filters[key];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      onFiltersChange({ [key]: next });
    },
    [filters, onFiltersChange]
  );

  // SCRUM-365: show the backend currency's symbol only — no 'USD' fallback.
  const sym = currencySymbol(currency);
  const isPriceDefault = localPrice[0] === 0 && localPrice[1] === priceMax;
  const priceLabel = isPriceDefault
    ? 'Any price'
    : `${sym}${localPrice[0].toLocaleString()} – ${sym}${localPrice[1].toLocaleString()}`;

  const activeCount = [
    filters.sortBy !== 'RELEVANCE',
    filters.priceMin != null || filters.priceMax != null,
    filters.offeringType !== 'ALL',
    filters.gradeBand.length > 0,
    filters.language.length > 0,
    filters.curriculum.length > 0,
    !!filters.availabilityTimeOfDay,
    filters.trialSessionAvailable === true,
    !!filters.targetExamId,
    !!filters.topicKeywords,
    hasLocation && radiusKm !== DEFAULT_RADIUS_KM,
  ].filter(Boolean).length;

  return (
    <aside className="w-full">
      {/* Single card wrapping all filters */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">

        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/50 bg-muted/20">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-bold text-slate-800">Filters</span>
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                {activeCount}
              </span>
            )}
          </div>
          {activeCount > 0 && (
            <button
              onClick={() => onFiltersChange({ ...DEFAULT_FILTERS })}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>

        <div className="px-5 py-4 space-y-5">

          {/* Sort by */}
          <section>
            <SectionLabel>Sort by</SectionLabel>
            <Select value={filters.sortBy} onValueChange={v => onFiltersChange({ sortBy: v })}>
              <SelectTrigger className="w-full h-9 text-sm cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortItems.map(item => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <Divider />

          {/* Price range */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <SectionLabel>Price per session</SectionLabel>
              <span className={cn('text-xs font-semibold -mt-3 tabular-nums', isPriceDefault ? 'text-muted-foreground' : 'text-foreground')}>
                {priceLabel}
              </span>
            </div>
            <div className="px-1">
              <Slider
                value={localPrice}
                onValueChange={v => setLocalPrice(v as [number, number])}
                onValueCommit={v => {
                  const [min, max] = v as [number, number];
                  onFiltersChange({ priceMin: min === 0 ? null : min, priceMax: max === priceMax ? null : max });
                }}
                min={0}
                max={priceMax}
                step={10}
                className="motion-safe:transition-none"
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{sym}0</span>
              <span>{sym}{priceMax.toLocaleString()}</span>
            </div>
          </section>

          <Divider />

          {/* Free trial */}
          <section className="flex items-center justify-between">
            <Label htmlFor="trial-toggle" className="text-sm font-medium cursor-pointer">
              Free trial available
            </Label>
            <Switch
              id="trial-toggle"
              checked={filters.trialSessionAvailable === true}
              onCheckedChange={checked => onFiltersChange({ trialSessionAvailable: checked ? true : null })}
              className="cursor-pointer"
            />
          </section>

          <Divider />

          {/* Offering type — hidden on per-type search pages (type fixed by route) */}
          {!hideOfferingType && (
            <>
              <section>
                <SectionLabel>Offering type</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {(['ALL', 'ONE_TO_ONE', 'GROUP', 'CRASH_COURSE'] as const).map(v => (
                    <ChipToggle
                      key={v}
                      label={v === 'ALL' ? 'All' : v === 'ONE_TO_ONE' ? 'One-to-One' : v === 'GROUP' ? 'Group' : 'Crash Course'}
                      active={filters.offeringType === v}
                      onClick={() => onFiltersChange({ offeringType: v })}
                    />
                  ))}
                </div>
              </section>
              <Divider />
            </>
          )}

          {/* Per-type / caller-supplied extra filter sections (offering pages) */}
          {extraSections}

          {/* Grade band */}
          <section>
            <SectionLabel>Grade band</SectionLabel>
            {gradeBands.isError ? (
              <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
            ) : (
              <MultiSelectChips
                options={gradeBands.entries}
                selected={filters.gradeBand}
                onToggle={code => toggleArray('gradeBand', code)}
                placeholder="Any grade"
              />
            )}
          </section>

          {/* Language */}
          <Divider />
          <section>
            <SectionLabel>Language</SectionLabel>
            {languages.isError ? (
              <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
            ) : (
              <MultiSelectChips
                options={languages.entries}
                selected={filters.language}
                onToggle={code => toggleArray('language', code)}
                placeholder="Any language"
              />
            )}
          </section>

          {/* Curriculum */}
          <Divider />
          <section>
            <SectionLabel>Curriculum</SectionLabel>
            {curricula.isError ? (
              <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
            ) : (
              <MultiSelectChips
                options={curricula.entries}
                selected={filters.curriculum}
                onToggle={code => toggleArray('curriculum', code)}
                placeholder="Any curriculum"
              />
            )}
          </section>

          <Divider />

          {/* Availability */}
          <section>
            <SectionLabel>Availability</SectionLabel>
            <Select
              value={filters.availabilityTimeOfDay || '__any__'}
              onValueChange={v => onFiltersChange({ availabilityTimeOfDay: v === '__any__' ? '' : v })}
            >
              <SelectTrigger className="w-full h-9 text-sm cursor-pointer">
                <SelectValue placeholder="Any time" />
              </SelectTrigger>
              <SelectContent>
                {AVAILABILITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value || '__any__'} value={opt.value || '__any__'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          {/* Distance */}
          <Divider />
          <section>
            <SectionLabel>Distance</SectionLabel>
            {hasLocation ? (
              <div className="flex flex-col gap-0.5 -mx-2.5">
                {RADIUS_OPTIONS.map(opt => (
                  <RadioOption
                    key={opt.value}
                    label={opt.label}
                    checked={radiusKm === opt.value}
                    onChange={() => onRadiusChange?.(opt.value)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground leading-relaxed">
                Enter a location in the search bar to filter by distance.
              </p>
            )}
          </section>

          {/* Target exam */}
          <Divider />
          <section>
            <SectionLabel>Target exam</SectionLabel>
            <Select
              value={filters.targetExamId || '__none__'}
              onValueChange={v => onFiltersChange({ targetExamId: v === '__none__' ? '' : v })}
              disabled={targetExamsLoading}
            >
              <SelectTrigger className="w-full h-9 text-sm cursor-pointer">
                <SelectValue placeholder="Any exam" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Any exam</SelectItem>
                {targetExams.map(exam => (
                  <SelectItem key={exam.id} value={exam.id}>{exam.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          {!hideTopicKeywords && (
            <>
              <Divider />
              {/* Topic keywords */}
              <section>
                <SectionLabel>Topic keywords</SectionLabel>
                <Input
                  value={filters.topicKeywords}
                  onChange={e => onFiltersChange({ topicKeywords: e.target.value })}
                  placeholder="e.g. calculus, algebra"
                  className="h-9 text-sm"
                />
              </section>
            </>
          )}

        </div>
      </div>
    </aside>
  );
}
